import React, { useState, useEffect, useCallback, memo, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { parseApiError } from '../../utils/errorUtils';
import { translateBackendMessage } from '../../utils/backendMessages';
import './EditableBOMItems.css';

// Alternatives (#322): lines with the same alternative_group replace each other. The BOM cost counts one per
// group: the main line (is_primary_alternative), else the first by sequence. Same rule as the backend
// (bom/alternatives.py counted_line_sql).
export const countedLineIds = (items = []) => {
  const groups = {};
  items.forEach(item => {
    if (item.alternative_group) (groups[item.alternative_group] = groups[item.alternative_group] || []).push(item);
  });
  const counted = new Set(items.filter(item => !item.alternative_group).map(item => item.bom_item_id));
  Object.values(groups).forEach(members => {
    const [main] = [...members].sort((a, b) =>
      (b.is_primary_alternative ? 1 : 0) - (a.is_primary_alternative ? 1 : 0)
      || (parseInt(a.sequence_order) || 0) - (parseInt(b.sequence_order) || 0)
      || (parseInt(a.bom_item_id) || 0) - (parseInt(b.bom_item_id) || 0));
    counted.add(main.bom_item_id);
  });
  return counted;
};

// BOM cost: the sum of the counted lines
export const bomTotalCost = (items = []) => {
  const counted = countedLineIds(items);
  return items.reduce((sum, item) =>
    counted.has(item.bom_item_id) ? sum + parseFloat(item.extended_cost || lineCost(item) || 0) : sum, 0);
};

// Line cost = quantity × unit cost × (1 + waste %), same as bom_items.extended_cost in the database
export const lineCost = (item) => {
  const quantity = parseFloat(item.quantity_per_unit) || 0;
  const unitCost = parseFloat(item.unit_cost) || 0;
  const waste = parseFloat(item.waste_percentage) || 0;
  return quantity * unitCost * (1 + waste / 100);
};

// Production Scaling Preview Component
const ProductionScalingPreview = memo(({ items, materials }) => {
  const { t } = useTranslation('production');
  const [productionQuantity, setProductionQuantity] = useState(100);
  const [isExpanded, setIsExpanded] = useState(false);

  // Calculate scaled quantities
  const scaledItems = useMemo(() => {
    return items.map(item => {
      const perUnitQty = parseFloat(item.quantity_per_unit || 0);
      const totalQty = perUnitQty * productionQuantity;

      // Find material info
      const itemId = item.item_id || item.inventory_item_id;
      const material = materials.find(m => m.item_id === itemId);

      return {
        ...item,
        materialName: material ? `${material.item_name} (${material.item_code})` : t('editableBomItems.itemFallback', { id: itemId }),
        perUnitQty,
        totalQty,
        unitCost: parseFloat(item.unit_cost || 0),
        totalCost: lineCost(item) * productionQuantity
      };
    });
  }, [items, materials, productionQuantity, t]);

  const totalMaterialCost = scaledItems.reduce((sum, item) => sum + item.totalCost, 0);

  if (items.length === 0) return null;

  return (
    <div className="production-scaling-preview">
      <div className="preview-header" onClick={() => setIsExpanded(!isExpanded)}>
        <h4>🔧 {t('editableBomItems.scaling.title')}</h4>
        <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
      </div>

      <div className="preview-controls">
        <div className="quantity-input">
          <label>{t('editableBomItems.scaling.productionQuantity')}</label>
          <input
            type="number"
            value={productionQuantity}
            onChange={(e) => setProductionQuantity(Math.max(1, parseInt(e.target.value) || 1))}
            min="1"
            step="1"
          />
          <span className="unit-label">{t('editableBomItems.scaling.units')}</span>
        </div>
        <div className="total-cost">
          <strong>{t('editableBomItems.totalMaterialCost')} ${totalMaterialCost.toFixed(2)}</strong>
        </div>
      </div>

      {isExpanded && (
        <div className="scaling-table-wrapper">
          <table className="scaling-table">
            <thead>
              <tr>
                <th>{t('common:ui.material')}</th>
                <th>{t('editableBomItems.scaling.perUnit')}</th>
                <th>{t('editableBomItems.scaling.totalFor', { count: productionQuantity })}</th>
                <th>{t('editableBomItems.scaling.totalCost')}</th>
              </tr>
            </thead>
            <tbody>
              {scaledItems.map((item, index) => (
                <tr key={item.bom_item_id || index}>
                  <td className="material-name">{item.materialName}</td>
                  <td className="per-unit">
                    {item.perUnitQty.toFixed(3)} {item.unit_of_measure || 'EA'}
                  </td>
                  <td className="total-qty">
                    <strong>{item.totalQty.toFixed(3)} {item.unit_of_measure || 'EA'}</strong>
                  </td>
                  <td className="total-cost">
                    ${item.totalCost.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td colSpan="3"><strong>{t('editableBomItems.totalMaterialCost')}</strong></td>
                <td><strong>${totalMaterialCost.toFixed(2)}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div className="preview-help">
        <small>
          💡 {t('editableBomItems.scaling.help')}
        </small>
      </div>
    </div>
  );
});

ProductionScalingPreview.displayName = 'ProductionScalingPreview';

// BOM Verification Tools Component
const BOMVerificationTools = memo(({ items, materials }) => {
  const { t } = useTranslation('production');
  const [isExpanded, setIsExpanded] = useState(false);

  // Verification checks
  const verificationResults = useMemo(() => {
    const issues = [];
    const warnings = [];
    const summary = {
      totalItems: items.length,
      itemsWithCosts: 0,
      totalEstimatedCost: 0,
      missingMaterials: 0,
      zeroQuantityItems: 0,
      highCostItems: []
    };

    const counted = countedLineIds(items);
    items.forEach((item, index) => {
      const itemId = item.item_id || item.inventory_item_id;
      const material = materials.find(m => m.item_id === itemId);
      const quantity = parseFloat(item.quantity_per_unit || 0);
      const unitCost = parseFloat(item.unit_cost || 0);
      const extendedCost = lineCost(item);

      // Check for missing materials.
      // If item_name is already set it means the BOM item was loaded from the
      // database (via JOIN on inventory_items), so the material exists even if
      // it is not in the local 50-item search cache.  Only flag items that have
      // no backend-supplied name AND are absent from the local cache.
      if (!material && !item.item_name && itemId) {
        issues.push({
          type: 'error',
          message: t('editableBomItems.verify.materialNotFound', { n: index + 1, id: itemId }),
          suggestion: t('editableBomItems.verify.materialNotFoundHint')
        });
        summary.missingMaterials++;
      }

      // Check for zero or negative quantities
      if (quantity <= 0) {
        issues.push({
          type: 'error',
          message: t('editableBomItems.verify.invalidQuantity', { n: index + 1, qty: quantity }),
          suggestion: t('editableBomItems.verify.invalidQuantityHint')
        });
        summary.zeroQuantityItems++;
      }

      // Check for missing unit of measure
      if (!item.unit_of_measure || item.unit_of_measure.trim() === '') {
        warnings.push({
          type: 'warning',
          message: t('editableBomItems.verify.missingUom', { n: index + 1 }),
          suggestion: t('editableBomItems.verify.missingUomHint')
        });
      }

      // Cost analysis
      if (unitCost > 0) {
        summary.itemsWithCosts++;
        if (counted.has(item.bom_item_id)) summary.totalEstimatedCost += extendedCost;

        // Flag high-cost items (more than 30% of total cost)
        if (extendedCost > summary.totalEstimatedCost * 0.3) {
          summary.highCostItems.push({
            index: index + 1,
            materialName: material?.item_name || item.item_name || t('editableBomItems.verify.highCostFallback', { id: itemId }),
            cost: extendedCost,
            percentage: (extendedCost / summary.totalEstimatedCost * 100).toFixed(1)
          });
        }
      } else if (quantity > 0) {
        warnings.push({
          type: 'warning',
          message: t('editableBomItems.verify.missingCost', { n: index + 1 }),
          suggestion: t('editableBomItems.verify.missingCostHint')
        });
      }

      // Check for very small quantities that might indicate unit errors
      if (quantity > 0 && quantity < 0.0001) {
        warnings.push({
          type: 'warning',
          message: t('editableBomItems.verify.smallQuantity', { n: index + 1, qty: quantity }),
          suggestion: t('editableBomItems.verify.smallQuantityHint')
        });
      }

      // Check for very large quantities that might indicate errors
      if (quantity > 1000) {
        warnings.push({
          type: 'warning',
          message: t('editableBomItems.verify.largeQuantity', { n: index + 1, qty: quantity }),
          suggestion: t('editableBomItems.verify.largeQuantityHint')
        });
      }
    });

    // Overall BOM checks
    if (items.length === 0) {
      issues.push({
        type: 'error',
        message: t('editableBomItems.verify.emptyBom'),
        suggestion: t('editableBomItems.verify.emptyBomHint')
      });
    }

    if (summary.totalEstimatedCost === 0 && items.length > 0) {
      warnings.push({
        type: 'warning',
        message: t('editableBomItems.verify.noCostInfo'),
        suggestion: t('editableBomItems.verify.noCostInfoHint')
      });
    }

    return {
      issues,
      warnings,
      summary,
      isValid: issues.length === 0,
      hasWarnings: warnings.length > 0
    };
  }, [items, materials, t]);

  const getStatusColor = (results) => {
    if (!results.isValid) return '#dc3545'; // Red for errors
    if (results.hasWarnings) return '#ffc107'; // Yellow for warnings
    return '#28a745'; // Green for all good
  };

  const getStatusText = (results) => {
    if (!results.isValid) return t('editableBomItems.verify.statusIssues');
    if (results.hasWarnings) return t('editableBomItems.verify.statusWarnings');
    return t('editableBomItems.verify.statusOk');
  };

  if (items.length === 0) return null;

  return (
    <div className="bom-verification-tools">
      <div className="verification-header" onClick={() => setIsExpanded(!isExpanded)}>
        <h4>
          🔍 {t('editableBomItems.verify.title')}
          <span
            className="verification-status"
            style={{ color: getStatusColor(verificationResults) }}
          >
            ({getStatusText(verificationResults)})
          </span>
        </h4>
        <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
      </div>

      <div className="verification-summary">
        <div className="summary-stats">
          <div className="stat-item">
            <span className="stat-label">{t('editableBomItems.verify.items')}</span>
            <span className="stat-value">{verificationResults.summary.totalItems}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">{t('editableBomItems.verify.estimatedCost')}</span>
            <span className="stat-value">${verificationResults.summary.totalEstimatedCost.toFixed(4)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">{t('editableBomItems.verify.issues')}</span>
            <span className="stat-value error">{verificationResults.issues.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">{t('editableBomItems.verify.warnings')}</span>
            <span className="stat-value warning">{verificationResults.warnings.length}</span>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="verification-details">
          {/* Issues */}
          {verificationResults.issues.length > 0 && (
            <div className="verification-section">
              <h5 className="section-title error">❌ {t('editableBomItems.verify.issuesTitle')}</h5>
              {verificationResults.issues.map((issue, index) => (
                <div key={index} className="verification-item error">
                  <div className="item-message">{issue.message}</div>
                  <div className="item-suggestion">{issue.suggestion}</div>
                </div>
              ))}
            </div>
          )}

          {/* Warnings */}
          {verificationResults.warnings.length > 0 && (
            <div className="verification-section">
              <h5 className="section-title warning">⚠️ {t('editableBomItems.verify.warningsTitle')}</h5>
              {verificationResults.warnings.map((warning, index) => (
                <div key={index} className="verification-item warning">
                  <div className="item-message">{warning.message}</div>
                  <div className="item-suggestion">{warning.suggestion}</div>
                </div>
              ))}
            </div>
          )}

          {/* Success message */}
          {verificationResults.isValid && !verificationResults.hasWarnings && (
            <div className="verification-section">
              <h5 className="section-title success">✅ {t('editableBomItems.verify.passedTitle')}</h5>
              <div className="verification-item success">
                <div className="item-message">{t('editableBomItems.verify.passedMessage')}</div>
                <div className="item-suggestion">
                  {t('editableBomItems.verify.passedDetail')}
                </div>
              </div>
            </div>
          )}

          {/* BOM Health Summary */}
          <div className="verification-section">
            <h5 className="section-title">📊 {t('editableBomItems.verify.healthTitle')}</h5>
            <div className="health-grid">
              <div className="health-item">
                <span className="health-label">{t('editableBomItems.verify.totalItems')}</span>
                <span className="health-value">{verificationResults.summary.totalItems}</span>
              </div>
              <div className="health-item">
                <span className="health-label">{t('editableBomItems.verify.itemsWithCosts')}</span>
                <span className="health-value">
                  {verificationResults.summary.itemsWithCosts} / {verificationResults.summary.totalItems}
                  ({((verificationResults.summary.itemsWithCosts / verificationResults.summary.totalItems) * 100).toFixed(0)}%)
                </span>
              </div>
              <div className="health-item">
                <span className="health-label">{t('editableBomItems.verify.costCoverage')}</span>
                <span className="health-value">
                  {verificationResults.summary.itemsWithCosts === verificationResults.summary.totalItems ? `✅ ${t('editableBomItems.verify.complete')}` : `⚠️ ${t('editableBomItems.verify.partial')}`}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="verification-help">
        <small>
          💡 {t('editableBomItems.verify.help')}
        </small>
      </div>
    </div>
  );
});

BOMVerificationTools.displayName = 'BOMVerificationTools';

// Memoized table row component - only re-renders when its specific item changes
const BOMItemRow = memo(({
  item,
  index,
  editMode,
  materials,
  loading,
  onItemChange,
  onSaveItem,
  onRemoveItem,
  onMaterialSearch,
  counts = true,
  canGroup = false,
  selected = false,
  onToggleSelect,
  onMakePrimary
}) => {
  const { t } = useTranslation('production');
  // Initialize and manage search text for material selection
  const [searchText, setSearchText] = useState('');

  // Calculate extended cost
  const extendedCost = useMemo(() => {
    return parseFloat(item.extended_cost || lineCost(item) || 0);
  }, [item.extended_cost, item.quantity_per_unit, item.unit_cost, item.waste_percentage]);

  // Memoize material display
  const materialDisplay = useMemo(() => {
    if (!editMode) {
      // Handle both item_id (from database) and inventory_item_id (frontend state)
      const itemId = item.item_id || item.inventory_item_id;
      // Materials use 'item_id' not 'inventory_item_id'
      const material = materials.find(m => m.item_id === itemId);
      if (material) {
        return `${material.item_name} (${material.item_code})`;
      } else if (item.item_name) {
        return `${item.item_name}${item.item_code ? ` (${item.item_code})` : ''}`;
      } else if (materials.length === 0) {
        return t('common:ui.loading');
      } else {
        return t('editableBomItems.materialNotFound', { id: itemId });
      }
    }
    return null;
  }, [editMode, materials, item.item_id, item.inventory_item_id, item.item_name, item.item_code, t]);

  // Update searchText when materials load or item changes
  useEffect(() => {
    if (!editMode) {
      setSearchText('');
      return;
    }

    // Handle both item_id (from database) and inventory_item_id (frontend state)
    const itemId = item.item_id || item.inventory_item_id;

    if (itemId) {
      // Materials use 'item_id' not 'inventory_item_id'
      const mat = materials.find(m => m.item_id === parseInt(itemId));
      if (mat) {
        setSearchText(`${mat.item_name} (${mat.item_code})`);
      } else if (item.item_name) {
        // Fallback to name from backend when material not in local paginated list
        setSearchText(`${item.item_name}${item.item_code ? ` (${item.item_code})` : ''}`);
      }
    }
    // Don't clear searchText when no itemId — user may be actively typing
  }, [item.item_id, item.inventory_item_id, materials, editMode]);

  return (
    <tr className={[item.isNew ? 'new-item' : item.isModified ? 'modified-item' : '',
      counts ? '' : 'alternative-not-counted'].join(' ').trim()}>
      <td>
        {canGroup && !item.isNew && (
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(item.bom_item_id)}
            aria-label={t('editableBomItems.alternatives.select')}
            style={{ marginRight: 6 }}
          />
        )}
        {editMode ? (
          <input
            type="number"
            value={item.sequence_order || index + 1}
            onChange={(e) => onItemChange(index, 'sequence_order', e.target.value)}
            className="form-input-inline"
            style={{ width: '50px' }}
          />
        ) : (
          index + 1
        )}
      </td>
      <td>
        {editMode ? (
          <div style={{ position: 'relative', minWidth: '200px' }}>
            <input
              type="text"
              list={`material-list-${index}`}
              value={searchText}
              onChange={(e) => {
                const value = e.target.value;
                setSearchText(value);
                if (onMaterialSearch) onMaterialSearch(value);

                // Try to find matching material
                const mat = materials.find(m =>
                  `${m.item_name} (${m.item_code})` === value ||
                  m.item_code === value ||
                  m.item_name === value
                );

                if (mat) {
                  // Update both item_id and inventory_item_id to ensure compatibility
                  // Materials use 'item_id' not 'inventory_item_id'
                  // Also auto-fill UOM and cost from material
                  onItemChange(index, 'inventory_item_id', mat.item_id, {
                    item_id: mat.item_id,
                    unit_of_measure: mat.unit_of_measure || 'EA',
                    unit_cost: parseFloat((mat.average_cost || mat.cost_per_unit || 0).toFixed(4)) || ''
                  });
                } else if (!value) {
                  onItemChange(index, 'inventory_item_id', '', {
                    item_id: '',
                    unit_of_measure: ''
                  });
                }
              }}
              placeholder={t('editableBomItems.searchMaterials')}
              className="form-input-inline"
              style={{ width: '100%' }}
              required
            />
            <datalist id={`material-list-${index}`}>
              {materials.map(mat => (
                <option
                  key={mat.item_id}
                  value={`${mat.item_name} (${mat.item_code})`}
                />
              ))}
            </datalist>
          </div>
        ) : (
          materialDisplay
        )}
      </td>
      <td>
        {editMode ? (
          <input
            type="number"
            value={item.quantity_per_unit || ''}
            onChange={(e) => onItemChange(index, 'quantity_per_unit', e.target.value)}
            className="form-input-inline"
            step="0.000001"
            min="0"
            required
          />
        ) : (
          parseFloat(item.quantity_per_unit || 0).toFixed(3)
        )}
      </td>
      <td>
        {/* UOM is read-only - auto-filled from material */}
        {item.unit_of_measure || '-'}
      </td>
      <td>
        {editMode ? (
          <input
            type="number"
            value={item.unit_cost || ''}
            onChange={(e) => onItemChange(index, 'unit_cost', e.target.value)}
            className="form-input-inline"
            step="0.0001"
            min="0"
          />
        ) : (
          `$${parseFloat(item.unit_cost || 0).toFixed(4)}`
        )}
      </td>
      <td>
        {editMode ? (
          <input
            type="number"
            value={item.waste_percentage ?? ''}
            onChange={(e) => onItemChange(index, 'waste_percentage', e.target.value)}
            className="form-input-inline"
            step="0.01"
            min="0"
            max="100"
            style={{ width: '70px' }}
          />
        ) : (
          `${parseFloat(item.waste_percentage || 0).toFixed(2)}%`
        )}
      </td>
      <td title={counts ? undefined : t('editableBomItems.alternatives.notCountedHint')}>
        ${extendedCost.toFixed(4)}
      </td>
      <td className="alternative-cell">
        {item.alternative_group ? (
          <>
            <span className={`alternative-badge ${item.is_primary_alternative || counts ? 'main' : ''}`}>
              {item.alternative_group}
              {' · '}
              {counts ? t('editableBomItems.alternatives.main') : t('editableBomItems.alternatives.alternative')}
            </span>
            {canGroup && !counts && (
              <button type="button" className="btn-link" onClick={() => onMakePrimary(item)}>
                {t('editableBomItems.alternatives.makeMain')}
              </button>
            )}
          </>
        ) : '-'}
      </td>
      <td>
        {editMode ? (
          <input
            type="text"
            value={item.notes || ''}
            onChange={(e) => onItemChange(index, 'notes', e.target.value)}
            className="form-input-inline"
            placeholder={t('editableBomItems.notesPlaceholder')}
          />
        ) : (
          item.notes || '-'
        )}
      </td>
      {editMode && (
        <td>
          <div className="action-buttons">
            {(item.isNew || item.isModified) && (
              <button
                onClick={() => onSaveItem(index)}
                className="btn-action save"
                title={t('common:ui.save')}
                disabled={loading}
              >
                ✓
              </button>
            )}
            <button
              onClick={() => onRemoveItem(index, item)}
              className="btn-action delete"
              title={t('common:ui.remove')}
              disabled={loading}
            >
              ×
            </button>
          </div>
        </td>
      )}
    </tr>
  );
});

// Add display name for debugging
BOMItemRow.displayName = 'BOMItemRow';

function EditableBOMItems({ bomId, items = [], onItemsChange, editMode = false }) {
  const { t } = useTranslation('production');
  const [localItems, setLocalItems] = useState(items);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [materials, setMaterials] = useState([]);
  const [materialsLoaded, setMaterialsLoaded] = useState(false);
  const [fetchingMaterials, setFetchingMaterials] = useState(false);
  const [materialSearchTerm, setMaterialSearchTerm] = useState('');
  const [debouncedMaterialSearch, setDebouncedMaterialSearch] = useState('');
  const isInternalUpdate = useRef(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [groupName, setGroupName] = useState('');

  useEffect(() => {
    // Skip sync when the change originated from local editing,
    // otherwise parent re-renders overwrite user input with stale data
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    setLocalItems(items);
  }, [items]);

  // Debounce material search — wait 300ms after typing stops
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedMaterialSearch(materialSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [materialSearchTerm]);

  // Fetch materials when debounced search changes
  useEffect(() => {
    fetchMaterials(debouncedMaterialSearch);
  }, [debouncedMaterialSearch]);

  const fetchMaterials = async (search = '') => {
    if (fetchingMaterials) return;

    setFetchingMaterials(true);
    try {
      const params = new URLSearchParams({ page_size: '50' });
      if (search) params.set('search', search);
      const response = await apiFetch(getApiUrl(`/api/inventory?${params}`), {
        cache: 'no-cache'
      });
      if (response.ok) {
        const data = await response.json();
        setMaterials(data.items || []);
        setMaterialsLoaded(true);
      }
    } catch (err) {
      console.error('Error fetching materials:', err);
    } finally {
      setFetchingMaterials(false);
    }
  };

  const handleAddItem = useCallback(() => {
    const newItem = {
      bom_item_id: `new_${Date.now()}`,
      bom_id: bomId,
      inventory_item_id: '',
      quantity_per_unit: 1,
      unit_of_measure: '',  // Will be auto-filled when material is selected
      sequence_order: localItems.length + 1,
      unit_cost: 0,
      extended_cost: 0,
      waste_percentage: 0,
      notes: '',
      is_optional: false,
      isNew: true
    };

    const updatedItems = [...localItems, newItem];
    setLocalItems(updatedItems);
    if (onItemsChange) {
      isInternalUpdate.current = true;
      onItemsChange(updatedItems);
    }
  }, [localItems, bomId, onItemsChange]);

  const handleRemoveItem = useCallback(async (index, item) => {
    if (!editMode) return;

    if (item.isNew) {
      const updatedItems = localItems.filter((_, i) => i !== index);
      setLocalItems(updatedItems);
      if (onItemsChange) {
        isInternalUpdate.current = true;
        onItemsChange(updatedItems);
      }
      return;
    }

    if (window.confirm(t('editableBomItems.confirmRemove'))) {
      try {
        setLoading(true);
        const response = await apiFetch(getApiUrl(`/api/boms/items/${item.bom_item_id}`), {
          method: 'DELETE'
        });

        if (response.ok) {
          const updatedItems = localItems.filter((_, i) => i !== index);
          setLocalItems(updatedItems);
          if (onItemsChange) {
            isInternalUpdate.current = true;
            onItemsChange(updatedItems);
          }
        } else {
          setError(t('editableBomItems.deleteFailed'));
        }
      } catch (err) {
        setError(t('editableBomItems.deleteError', { message: err.message }));
      } finally {
        setLoading(false);
      }
    }
  }, [editMode, localItems, onItemsChange, t]);

  // Optimized handleItemChange using functional state update
  const handleItemChange = useCallback((index, field, value, additionalFields = {}) => {
    setLocalItems(prevItems => {
      // Use map with Object.assign for better performance
      const updatedItems = prevItems.map((item, i) => {
        if (i !== index) return item;

        const updatedItem = Object.assign({}, item, {
          [field]: value,
          ...additionalFields,
          isModified: true
        });

        // Calculate extended cost if needed
        if (field === 'quantity_per_unit' || field === 'unit_cost' || field === 'waste_percentage') {
          updatedItem.extended_cost = parseFloat(lineCost(updatedItem).toFixed(4));
        }

        return updatedItem;
      });

      // Immediately call parent callback for critical fields
      if (onItemsChange && (field === 'inventory_item_id' || field === 'item_id')) {
        isInternalUpdate.current = true;
        onItemsChange(updatedItems);
      }

      return updatedItems;
    });

    // Debounce the parent callback only for non-critical fields
    if (onItemsChange && field !== 'inventory_item_id' && field !== 'item_id') {
      clearTimeout(handleItemChange.timeoutId);
      handleItemChange.timeoutId = setTimeout(() => {
        setLocalItems(current => {
          isInternalUpdate.current = true;
          onItemsChange(current);
          return current;
        });
      }, 300);
    }
  }, [onItemsChange]);

  const handleSaveItem = useCallback(async (index) => {
    try {
      setLoading(true);
      setError('');

      // Get the latest item from state instead of using the parameter
      const item = localItems[index];

      // Validate required fields
      // Handle both item_id (from database) and inventory_item_id (frontend state)
      const itemIdValue = item.item_id || item.inventory_item_id;
      const itemId = parseInt(itemIdValue);

      if (!itemIdValue || isNaN(itemId)) {
        setError(t('editableBomItems.selectMaterial'));
        setLoading(false);
        return;
      }

      if (!item.quantity_per_unit || parseFloat(item.quantity_per_unit) <= 0) {
        setError(t('editableBomItems.quantityPositive'));
        setLoading(false);
        return;
      }

      let response;
      if (item.isNew) {
        const { isNew, bom_item_id, ...itemData } = item;
        const requestBody = {
          item_id: itemId,
          quantity_per_unit: parseFloat(parseFloat(itemData.quantity_per_unit || 0).toFixed(6)),
          unit_of_measure: itemData.unit_of_measure || 'EA',
          unit_cost: parseFloat(parseFloat(itemData.unit_cost || 0).toFixed(4)),
          waste_percentage: parseFloat(parseFloat(itemData.waste_percentage || 0).toFixed(2)),
          sequence_order: parseInt(itemData.sequence_order) || 1,
          notes: itemData.notes || null,
          is_optional: itemData.is_optional || false
        };

        response = await apiFetch(getApiUrl(`/api/boms/${bomId}/items`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });
      } else if (item.isModified) {
        const { isModified, ...itemData } = item;
        const updateBody = {
          item_id: itemId,
          quantity_per_unit: parseFloat(parseFloat(itemData.quantity_per_unit || 0).toFixed(6)),
          unit_of_measure: itemData.unit_of_measure,
          unit_cost: parseFloat(parseFloat(itemData.unit_cost || 0).toFixed(4)),
          waste_percentage: parseFloat(parseFloat(itemData.waste_percentage || 0).toFixed(2)),
          sequence_order: parseInt(itemData.sequence_order) || 1,
          notes: itemData.notes,
          is_optional: itemData.is_optional
        };

        response = await apiFetch(getApiUrl(`/api/boms/items/${item.bom_item_id}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updateBody)
        });
      }

      if (response && response.ok) {
        const savedItem = await response.json();
        setLocalItems(prevItems => {
          const updatedItems = [...prevItems];
          updatedItems[index] = {
            ...savedItem,
            isModified: false,
            isNew: false
          };
          if (onItemsChange) {
            isInternalUpdate.current = true;
            onItemsChange(updatedItems);
          }
          return updatedItems;
        });
      } else {
        setError(t('editableBomItems.saveFailed'));
      }
    } catch (err) {
      setError(t('editableBomItems.saveError', { message: err.message }));
    } finally {
      setLoading(false);
    }
  }, [bomId, onItemsChange, localItems, t]);

  // Calculate total only when items change: one line per group of alternatives (#322)
  const totalCost = useMemo(() => bomTotalCost(localItems), [localItems]);
  const counted = useMemo(() => countedLineIds(localItems), [localItems]);
  const canGroup = editMode && !!bomId;

  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  }, []);

  // Group / ungroup lines as alternatives and choose the main one (PUT /api/boms/{id}/alternatives)
  const saveAlternatives = useCallback(async (ids, group, primaryId) => {
    try {
      setLoading(true);
      setError('');
      const response = await apiFetch(getApiUrl(`/api/boms/${bomId}/alternatives`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bom_item_ids: ids, alternative_group: group, primary_bom_item_id: primaryId ?? null })
      });
      if (!response.ok) {
        const parsed = parseApiError(await response.json().catch(() => ({})));
        setError(parsed.message ? translateBackendMessage(parsed.message) : t('editableBomItems.alternatives.saveFailed'));
        return;
      }
      const saved = Object.fromEntries((await response.json()).map(it => [it.bom_item_id, it]));
      setLocalItems(prev => {
        const updated = prev.map(it => (saved[it.bom_item_id]
          ? { ...it, alternative_group: saved[it.bom_item_id].alternative_group,
              is_primary_alternative: saved[it.bom_item_id].is_primary_alternative }
          : it));
        if (onItemsChange) {
          isInternalUpdate.current = true;
          onItemsChange(updated);
        }
        return updated;
      });
      setSelectedIds([]);
    } catch (err) {
      setError(t('editableBomItems.saveError', { message: err.message }));
    } finally {
      setLoading(false);
    }
  }, [bomId, onItemsChange, t]);

  const handleGroup = () => {
    const name = groupName.trim() || t('editableBomItems.alternatives.defaultName');
    saveAlternatives(selectedIds, name, selectedIds[0]);
  };
  const handleUngroup = () => saveAlternatives(selectedIds, null);
  const handleMakePrimary = useCallback((item) => {
    const ids = localItems.filter(it => it.alternative_group === item.alternative_group).map(it => it.bom_item_id);
    saveAlternatives(ids, item.alternative_group, item.bom_item_id);
  }, [localItems, saveAlternatives]);

  if (!editMode && (!items || items.length === 0)) {
    return <div className="no-data">{t('editableBomItems.noItems')}</div>;
  }

  return (
    <div className="editable-bom-items">
      {error && <div className="error-message">{String(error)}</div>}

      <div className="items-header">
        {editMode && (
          <button
            className="btn btn-primary btn-sm"
            onClick={handleAddItem}
            disabled={loading}
          >
            {t('editableBomItems.addItem')}
          </button>
        )}
      </div>

      {canGroup && localItems.length > 1 && (
        <div className="alternatives-toolbar">
          <span className="alternatives-help">{t('editableBomItems.alternatives.help')}</span>
          <input
            type="text"
            className="form-input-inline"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder={t('editableBomItems.alternatives.groupPlaceholder')}
            maxLength={50}
            aria-label={t('editableBomItems.alternatives.groupName')}
          />
          <button type="button" className="btn btn-secondary btn-sm" onClick={handleGroup}
            disabled={loading || selectedIds.length < 2}>
            {t('editableBomItems.alternatives.group')}
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={handleUngroup}
            disabled={loading || selectedIds.length === 0}>
            {t('editableBomItems.alternatives.ungroup')}
          </button>
        </div>
      )}

      {/* Production Quantity Scaling Preview */}
      {localItems.length > 0 && (
        <ProductionScalingPreview items={localItems} materials={materials} />
      )}

      {/* BOM Verification Tools */}
      {localItems.length > 0 && (
        <BOMVerificationTools items={localItems} materials={materials} />
      )}

      <div className="items-table-wrapper">
        <table className="items-table">
          <thead>
            <tr>
              <th>#</th>
              <th>{t('common:ui.material')}</th>
              <th>{t('editableBomItems.columns.quantityPerUnit')}</th>
              <th>{t('editableBomItems.columns.uom')}</th>
              <th>{t('editableBomItems.columns.unitCost')}</th>
              <th>{t('editableBomItems.columns.wastePct')}</th>
              <th>{t('editableBomItems.columns.extendedCost')}</th>
              <th>{t('editableBomItems.columns.alternative')}</th>
              <th>{t('common:ui.notes')}</th>
              {editMode && <th>{t('common:ui.actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {localItems.map((item, index) => (
              <BOMItemRow
                key={item.bom_item_id}
                item={item}
                index={index}
                editMode={editMode}
                materials={materials}
                loading={loading}
                onItemChange={handleItemChange}
                onSaveItem={handleSaveItem}
                onRemoveItem={handleRemoveItem}
                onMaterialSearch={setMaterialSearchTerm}
                counts={counted.has(item.bom_item_id)}
                canGroup={canGroup}
                selected={selectedIds.includes(item.bom_item_id)}
                onToggleSelect={toggleSelect}
                onMakePrimary={handleMakePrimary}
              />
            ))}
          </tbody>
          <tfoot>
            <tr className="total-row">
              <td colSpan={6} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                {t('editableBomItems.totalMaterialCost')}
              </td>
              <td style={{ fontWeight: 'bold' }}>
                ${totalCost.toFixed(4)}
              </td>
              <td colSpan={editMode ? 3 : 2}></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default EditableBOMItems;